// src/hooks/useDiscoverFilters.ts
//
// Filter and sort preferences for the Discover feed. Per-device
// (localStorage) rather than per-account because filters are a UI
// preference; signing in on a new device shouldn't import "I had
// Religious turned off in Tokyo last week" by surprise.
//
// State:
//   enabledCategories — Set of category keys that pass the filter.
//                       Default: all keys enabled.
//   hideClosed       — When true, items reporting is_open_now=false
//                       are filtered out. Items where the open status
//                       is unknown are always shown (LLM-curated picks
//                       and items without a place_id rarely have it).
//   sortBy           — 'distance' | 'newest' | 'rating'.
//                       Default: 'distance' (closest first).

import { useCallback, useEffect, useState } from 'react';
import { ALL_CATEGORY_KEYS } from '../utils/discoverCategories';

export type DiscoverSortKey = 'distance' | 'newest' | 'rating';

export interface DiscoverFilters {
  enabledCategories: Set<string>;
  hideClosed: boolean;
  sortBy: DiscoverSortKey;
}

const STORAGE_KEY = 'instant-tours:discover-filters:v1';

interface PersistedShape {
  enabledCategories: string[];
  hideClosed: boolean;
  sortBy: DiscoverSortKey;
}

function loadFromStorage(): DiscoverFilters {
  if (typeof window === 'undefined') {
    return defaults();
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaults();
    const parsed = JSON.parse(raw) as PersistedShape;
    if (!parsed || typeof parsed !== 'object') return defaults();
    return {
      enabledCategories: new Set(
        Array.isArray(parsed.enabledCategories)
          ? parsed.enabledCategories
          : Array.from(ALL_CATEGORY_KEYS)
      ),
      hideClosed: !!parsed.hideClosed,
      sortBy:
        parsed.sortBy === 'newest' ||
        parsed.sortBy === 'rating' ||
        parsed.sortBy === 'distance'
          ? parsed.sortBy
          : 'distance',
    };
  } catch {
    return defaults();
  }
}

function defaults(): DiscoverFilters {
  return {
    enabledCategories: new Set(ALL_CATEGORY_KEYS),
    hideClosed: false,
    sortBy: 'distance',
  };
}

function saveToStorage(state: DiscoverFilters): void {
  if (typeof window === 'undefined') return;
  try {
    const payload: PersistedShape = {
      enabledCategories: Array.from(state.enabledCategories),
      hideClosed: state.hideClosed,
      sortBy: state.sortBy,
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage full or disabled — silently ignore. Filters
    // remain in-memory for the session.
  }
}

export function useDiscoverFilters() {
  const [filters, setFilters] = useState<DiscoverFilters>(loadFromStorage);

  // Persist on every change.
  useEffect(() => {
    saveToStorage(filters);
  }, [filters]);

  const toggleCategory = useCallback((key: string) => {
    setFilters((prev) => {
      const next = new Set(prev.enabledCategories);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return { ...prev, enabledCategories: next };
    });
  }, []);

  const setAllCategories = useCallback((enabled: boolean) => {
    setFilters((prev) => ({
      ...prev,
      enabledCategories: enabled ? new Set(ALL_CATEGORY_KEYS) : new Set(),
    }));
  }, []);

  const setHideClosed = useCallback((hide: boolean) => {
    setFilters((prev) => ({ ...prev, hideClosed: hide }));
  }, []);

  const setSortBy = useCallback((sort: DiscoverSortKey) => {
    setFilters((prev) => ({ ...prev, sortBy: sort }));
  }, []);

  const resetToDefaults = useCallback(() => {
    setFilters(defaults());
  }, []);

  /** True when at least one filter has been narrowed from defaults.
   *  Used to show a count badge on the filter chip. */
  const isFilterActive =
    filters.enabledCategories.size < ALL_CATEGORY_KEYS.size ||
    filters.hideClosed ||
    filters.sortBy !== 'distance';

  /** Number of categories EXCLUDED from the default everything-on
   *  state. Used as a badge count on the filter chip. 0 if every
   *  category is on (the default). Includes a +1 if hideClosed is
   *  on, so the user sees at-a-glance how many adjustments they've
   *  made. */
  const filterAdjustmentCount =
    (ALL_CATEGORY_KEYS.size - filters.enabledCategories.size) +
    (filters.hideClosed ? 1 : 0) +
    (filters.sortBy !== 'distance' ? 1 : 0);

  return {
    filters,
    toggleCategory,
    setAllCategories,
    setHideClosed,
    setSortBy,
    resetToDefaults,
    isFilterActive,
    filterAdjustmentCount,
  };
}
